import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../services/firebase';
import { User } from '../types';
import { useAuth } from './AuthContext';

interface ProfilesContextType {
  profiles: Record<string, User>;
  getProfile: (userId: string) => User | null;
  subscribeToIds: (ids: string[]) => void;
  setTripProfiles: (travellers: User[], acceptedIds: string[]) => void;
}

/** `prev` itself when nothing is new, so React skips the update. */
export function withIds(prev: Set<string>, ids: string[]): Set<string> {
  if (ids.every(id => prev.has(id))) return prev;
  return new Set([...prev, ...ids]);
}

/** Only display fields from accepted co-travellers enter the shared profile cache. */
export function publicTripTraveller(user: User): User {
  return {
    id: user.id, name: user.name || 'Traveller', email: '', initials: user.initials || '??',
    color: user.color || '#666', emoji: user.emoji || '👤', imgSrc: user.imgSrc || null,
    photoURL: user.photoURL ?? null,
  };
}

export function publicTripProfiles(travellers: User[], acceptedIds: string[]): Record<string, User> {
  const accepted = new Set(acceptedIds);
  return Object.fromEntries(travellers
    .filter(user => accepted.has(user.id))
    .map(user => [user.id, publicTripTraveller(user)]));
}

const ProfilesContext = createContext<ProfilesContextType | undefined>(undefined);

export function ProfilesProvider({ children }: { children: React.ReactNode }) {
  const { firebaseUser } = useAuth();
  const [profiles, setProfiles] = useState<Record<string, User>>({});
  const [subscribedIds, setSubscribedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!firebaseUser) {
      if (Object.keys(profiles).length > 0) setProfiles({});
      if (subscribedIds.size > 0) setSubscribedIds(new Set());
      return;
    }
    
    if (subscribedIds.size === 0) return;

    const unsubscribes = Array.from(subscribedIds)
      .filter(userId => userId === firebaseUser.uid)
      .map((userId: string) => onSnapshot(doc(db, 'users', userId), (docSnap) => {
        if (docSnap.exists()) {
          setProfiles(prev => ({
            ...prev,
            [userId]: docSnap.data() as User
          }));
        }
      }, (error) => handleFirestoreError(error, OperationType.GET, `users/${userId}`)));

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [subscribedIds, firebaseUser]);

  // Functional and stable. Every UserAvatar calls this from an effect in the
  // same commit; building from the render's `subscribedIds` meant each call
  // started from the same stale set and only the last id stuck — and a new
  // function identity every render re-fired every avatar's effect until the
  // set converged, tearing down and re-creating every listener each round.
  const subscribeToIds = useCallback((ids: string[]) => setSubscribedIds(prev => withIds(prev, ids)), []);

  const setTripProfiles = useCallback((travellers: User[], acceptedIds: string[]) => {
    const shared = publicTripProfiles(travellers, acceptedIds);
    setProfiles(prev => ({ ...shared, ...(firebaseUser && prev[firebaseUser.uid]
      ? { [firebaseUser.uid]: prev[firebaseUser.uid] } : {}) }));
  }, [firebaseUser]);

  const getProfile = (userId: string) => {
    return profiles[userId] || null;
  };

  return (
    <ProfilesContext.Provider value={{ profiles, getProfile, subscribeToIds, setTripProfiles }}>
      {children}
    </ProfilesContext.Provider>
  );
}

export function useProfiles() {
  const context = useContext(ProfilesContext);
  if (context === undefined) {
    throw new Error('useProfiles must be used within a ProfilesProvider');
  }
  return context;
}
